import { useCallback, useEffect, useState } from 'react';
import { fetchVapidPublicKey, sendTestPush, subscribeOnServer, unsubscribeOnServer } from './api';
import { notificationPermission, supportsPush } from './platform';
import { createSubscription, getExistingSubscription, toPushSubscriptionJson } from './subscribe';

export interface PushSubscriptionState {
  supported: boolean;
  /** Whether the server has VAPID keys configured — subscribing is pointless without it. */
  serverConfigured: boolean | 'loading';
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
  busy: boolean;
  error: string | null;
}

export interface PushSubscriptionActions {
  /** Requests notification permission (if needed) then subscribes. Call this from a user
   * gesture (button click) — browsers reject permission requests otherwise. */
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  sendTest: () => Promise<void>;
}

// Module-level (not per-hook-instance) so a remount within the same app load doesn't
// re-fire the reconcile POST below — see the mount effect for why it exists at all.
let reconciledThisLoad = false;

/** Drives the notification-permission priming screen and the settings-page toggle. */
export function usePushSubscription(): PushSubscriptionState & PushSubscriptionActions {
  const supported = supportsPush();
  const [permission, setPermission] = useState(notificationPermission());
  const [serverConfigured, setServerConfigured] = useState<boolean | 'loading'>('loading');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supported) {
      setServerConfigured(false);
      return;
    }
    const controller = new AbortController();
    fetchVapidPublicKey(controller.signal)
      .then((key) => setServerConfigured(key !== null))
      .catch(() => setServerConfigured(false));
    getExistingSubscription()
      .then((sub) => {
        setSubscribed(sub !== null);
        // Self-heal: the browser's subscription is trusted forever today, but the server
        // side of it can go missing without the browser ever finding out — a repository
        // prune (a send came back 410 for a since-rotated endpoint), a restart that lost
        // the in-memory store, or a fresh DATABASE_URL migration. Re-POSTing on every load
        // re-registers it; `/api/push/subscribe` upserts on endpoint (routes.ts) so this
        // is a no-op when the row is already there. At most once per app load, and a
        // failure here never flips `subscribed` back off — the browser having a live
        // subscription is reason enough to show "on".
        if (sub && !reconciledThisLoad) {
          reconciledThisLoad = true;
          subscribeOnServer(toPushSubscriptionJson(sub)).catch(() => {
            // Swallow — see comment above. Next app load tries again.
          });
        }
      })
      .catch(() => setSubscribed(false));
    return () => controller.abort();
  }, [supported]);

  const subscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (!supported) throw new Error('Push notifications are not supported in this browser.');
      const permissionResult =
        Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      setPermission(permissionResult);
      if (permissionResult !== 'granted') {
        throw new Error('Notification permission was not granted.');
      }
      const vapidPublicKey = await fetchVapidPublicKey();
      if (!vapidPublicKey) throw new Error('Push is not configured on the server yet.');
      const subscription = await createSubscription(vapidPublicKey);
      await subscribeOnServer(toPushSubscriptionJson(subscription));
      setSubscribed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe.');
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const existing = await getExistingSubscription();
      if (existing) {
        await unsubscribeOnServer(existing.endpoint);
        await existing.unsubscribe();
      }
      setSubscribed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe.');
    } finally {
      setBusy(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await sendTestPush();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test push.');
    } finally {
      setBusy(false);
    }
  }, []);

  return {
    supported,
    serverConfigured,
    permission,
    subscribed,
    busy,
    error,
    subscribe,
    unsubscribe,
    sendTest,
  };
}
